import React, { useEffect, useMemo, useState } from 'react';
import { Footer } from 'src/components/Quiz/Footer';
import { InterestCheckBoxSet } from 'src/components/Quiz/InterestCheckBoxSet';
import { trackEvent } from 'src/services/analytics';
import { AlgoliaFacets } from 'src/clients/Algolia/constants';
import { useSearchContext } from 'src/components/Providers/SearchProvider';
import { QuizQuestion } from 'src/components/Quiz/QuizQuestion';
import {
	QuizAnalytics,
	ScreenProps,
	SubInterestCategory,
} from '@src/containers/QuizContainer/types';
import { getRemainingFacetsFromAnswers } from '@src/containers/QuizContainer/answerHelpers';
import { useQuiz } from '@src/components/Providers/QuizProvider';
import styles from './HealthcareInterests.module.scss';
import { healthcareInterestCategories } from './healthcareInterestCategories';

const question = 'What areas of healthcare are you most interested in?';

export const HealthcareInterests: React.FC<ScreenProps> = props => {
	const { filters, index } = useSearchContext();
	const { answers } = useQuiz();
	const { defaultAnswers, nextScreen, previousScreen, updateAnswers } = props;
	const [validInterests, setHealthcareInterests] = useState<SubInterestCategory[]>([]);

	const healthcareInterests = useMemo(() => {
		return answers?.healthcareInterests
			? answers.healthcareInterests
			: defaultAnswers?.healthcareInterests || [];
	}, [answers, defaultAnswers]);

	const analyticsData: QuizAnalytics = useMemo(() => {
		return {
			questionKey: question,
			questionType: 'Multiselect',
		};
	}, []);

	useEffect(() => {
		const fetchHealthcareInterests = async () => {
			const relevantAnswers = answers;

			if (relevantAnswers.healthcareInterests) {
				delete relevantAnswers.healthcareInterests;
			}

			const facets = await getRemainingFacetsFromAnswers(
				{ ...relevantAnswers, healthcareInterests: [] },
				`${AlgoliaFacets['Interests children']}`,
				filters,
				index
			);

			const availableSubInterests = healthcareInterestCategories.filter(category =>
				facets.includes(category.facet)
			);
			const lastHealthcareOption = healthcareInterestCategories.find(
				category => category.name === 'All healthcare programs'
			);
			setHealthcareInterests([...availableSubInterests, lastHealthcareOption]);
			updateAnswers({ healthcareInterests: [...healthcareInterests] }, answers);
		};

		fetchHealthcareInterests();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, index]);
	useEffect(() => {
		trackEvent('userProgramQuizQuestionAttempted', analyticsData);
	}, [analyticsData]);

	return (
		<div className={styles.container} data-testid='healthcare-interests'>
			<legend className={styles.title}>
				<QuizQuestion question={question} />
			</legend>
			<InterestCheckBoxSet
				interests={validInterests}
				onSelect={interests => updateAnswers({ healthcareInterests: interests }, answers)}
				selections={healthcareInterests}
			/>
			<Footer
				disableNext={!(answers.healthcareInterests || []).length}
				onClickNext={() => {
					analyticsData.responseKey = healthcareInterests;
					trackEvent('userProgramQuizQuestionCompleted', analyticsData);
					nextScreen();
				}}
				onClickPrevious={previousScreen}
			/>
		</div>
	);
};
